export const isArrayWithElements = (payload: unknown): payload is unknown[] => {
  return Array.isArray(payload) && payload.length >= 1;
};

export const getCookieByName = (name: string): string | undefined => {
  if (typeof window === "undefined") {
    return;
  }
  const match = document.cookie.match(new RegExp(name + "=([^;]+)"));
  return match ? decodeURIComponent(match[1]) : undefined;
};

export const isSameOrigin = (origin: string): boolean => {
  if (origin === "http://localhost:5173") return true;
  const matches = origin.match(/([^\\/.]+)\.sg/);
  if (matches && matches.length > 1) {
    return matches[1] === "beritaharian";
  }
  return false;
};

export const sanitizeSearchKeyword = (searchValue: string) => {
  const sanitizedInput = searchValue.replace(/[^\w\s]/gi, "").substring(0, 255);
  return sanitizedInput;
};

export const prefixCheck = (url: string): boolean => {
  if (url.startsWith("/") && !url.includes("://")) {
    return true;
  }
  return false;
};

import { ClientSideSecrets } from "@app/featureFlag/featureFlags";
import { SgCoords } from "@components/Modules/FoodMap/FoodMapConstants/FoodMapConstants";
import {
  MOBILE_SECTION_LIST_NEW,
  NON_HEADER_NEW,
  SECONDARY_SECTION_LIST_NEW,
  SECTION_LIST_NEW,
} from "@components/Modules/HeaderNew/headerMenuConstants";
import { VALID_PARENT_SECTION_NAMES } from "@components/Modules/HeaderNew/subCategoriesConstants";
import type { Point } from "@typings/foodMapTypes";

import fetchBHApi from "./fetchBHApi";
import { BH_BASE_URL, ENVIRONMENT } from "./getEnvVariables";
//iteration determine how frequent it would return true, useful for ads.
export const displayAfter = (index: number, iteration: number): boolean => {
  return index > 0 && index % iteration === 0;
};

export async function getFeatureFlag(key: ClientSideSecrets): Promise<string> {
  type Response = {
    Key: string;
  };
  const queryParams = new URLSearchParams({
    key,
  }).toString();
  const response: Response = await fetchBHApi(
    `get-feature-flag?${queryParams}`,
    "GET",
  );

  return response.Key;
}

export const findStyleBySection = (
  uniqueSectionName: string | undefined,
): { image?: string; selectedColor?: string } | undefined => {
  const sections = [
    ...SECONDARY_SECTION_LIST_NEW,
    ...SECTION_LIST_NEW,
    ...NON_HEADER_NEW,
  ];

  const result = sections.find(
    ({ url }) => url.slice(1).toLowerCase() === uniqueSectionName,
  );
  return { image: result?.images, selectedColor: result?.selectedColor };
};

export const findStyleBySectionNew = (
  uniqueSectionName: string | undefined,
): { image?: string; selectedColor?: string } | undefined => {
  const sections = [
    ...SECONDARY_SECTION_LIST_NEW,
    ...SECTION_LIST_NEW,
    ...NON_HEADER_NEW,
  ];

  const result = sections.find(
    ({ url }) => url.slice(1).toLowerCase() === uniqueSectionName,
  );
  return { image: result?.images, selectedColor: result?.selectedColor };
};

export const findStyleBySectionAndSubCategory = (
  sectionName: string,
): { selectedColor?: string } | undefined => {
  if (VALID_PARENT_SECTION_NAMES.includes(sectionName)) {
    const section = MOBILE_SECTION_LIST_NEW[sectionName];
    const colorItem = section.find((item) => item.selectedColor);
    return { selectedColor: colorItem?.selectedColor };
  } else {
    const sections = [
      ...SECONDARY_SECTION_LIST_NEW,
      ...SECTION_LIST_NEW,
      ...NON_HEADER_NEW,
    ];
    const result = sections.find(
      ({ title, url }) =>
        title === sectionName ||
        title.split(" ")[0] === sectionName.split(" ")[0] ||
        url.slice(1).toLowerCase() === sectionName,
    );
    return { selectedColor: result?.selectedColor };
  }
};

//Returns https://uat.beritaharian.sg as example
export const getWindowOrigin = (): string => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  } else if (ENVIRONMENT === "local") {
    return "http://localhost:5173";
  }
  return BH_BASE_URL;
};

export const isClientSide: boolean = typeof window !== "undefined";
export const isProd = "https://www.beritaharian.sg" === getWindowOrigin();

/**
 * Description of the function.
 *
 * @param {Type} callback - Callback for this function.
 * @param {Type} maxRetries - No. of tries wanted to do.
 * @param {Type} initialDelayMs - Initial delay for each retry.
 * @param {Type} maxDelayMs - Maximum allowed delay.
 */

export async function fetchWithRetry<T>(
  callback: () => Promise<T>,
  maxRetries: number,
  initialDelayMs: number,
  maxDelayMs: number,
): Promise<T> {
  let retries = 0;
  let delay = initialDelayMs;

  while (retries < maxRetries) {
    try {
      return await callback();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, maxDelayMs);
    }
  }

  throw new Error("Max retries exceeded");
}

export function getValidEmailRegex() {
  return /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
}

export function betweenCoords(x: number, min: number, max: number) {
  return x >= min && x <= max;
}

export const isLocationInSingapore = (location: Point) =>
  betweenCoords(location.lat, SgCoords.lat.bot, SgCoords.lat.top) &&
  betweenCoords(location.lng, SgCoords.lang.left, SgCoords.lang.right);

export function roundToFourDecimals(value: number): number {
  return Math.round(value * 10000) / 10000;
}

export const getRoundedCoords = (point?: Point | null) => ({
  lat: point ? roundToFourDecimals(point.lat) : null,
  lng: point ? roundToFourDecimals(point.lng) : null,
});

/**
 * Helper function to check if URL is a URL from the specified from parameter.
 * @param url URL to check.
 * @returns {boolean}
 */
export const isThisUrlFrom = (url: string, from: string): boolean => {
  const domain = new URL(url).hostname;
  return domain.includes(from);
};

export function getRandomValue() {
  // use crypto.getRandomValues() instead of Math.random()
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  const randomValue = array[0] / 2 ** 32; // normalize to [0, 1)

  return randomValue;
}

export function parseSortString(sortStr: string | undefined): number[] {
  if (typeof sortStr !== "string") return [];
  return sortStr
    .split(",")
    .map((item) => parseInt(item.trim(), 10))
    .filter((num) => !isNaN(num));
}

export function parseSizeInteger(
  input: string | undefined,
  defaultValue: number = 10,
): number {
  if (!input) return defaultValue;
  const num = Number(input);
  return Number.isInteger(num) && num > 0 ? num : defaultValue;
}

/**
 * Helper function to check if the user is on a mobile or tablet device.
 * @returns {boolean}
 */
export const isMobileOrTablet = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(
    userAgent,
  );
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(
    userAgent,
  );

  return isMobile || isTablet;
};

export function reactionCount(count: number): string {
  if (count >= 1_000_000_000) {
    return (count / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"; // Billions
  } else if (count >= 1_000_000) {
    return (count / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"; // Millions
  } else if (count >= 1_000) {
    return (count / 1_000).toFixed(1).replace(/\.0$/, "") + "K"; // Thousands
  }

  return count.toLocaleString();
}

export function normalizeSearchInput(search: string): string {
  if (!search) return "";
  // Trim leading/trailing spaces and collapse multiple spaces into one
  return search.trim().replace(/\s+/g, " ");
}

/**
 * Searches for a key in an object by matching a given value.
 * Returns the first matching key or `undefined` if not found.
 *
 * @param obj - The object to search through. Keys are strings, values are strings.
 * @param value - The value to search for.
 *
 * @returns The key corresponding to the value, or undefined if not found.
 */
export function getKeyByValue(
  obj: { [key: string]: string } | null | undefined,
  value: string | null | undefined,
): string | undefined {
  if (!obj || !value) return undefined; // Guard clause

  return Object.keys(obj).find((key) => obj[key] === value);
}

/**
 * Splits an array into smaller batches of a given size.
 *
 * @template T - Type of items in the input array
 * @param items - The full list of items to be chunked
 * @param size - Maximum number of items allowed per batch
 * @returns An array of batches, where each batch is an array of items
 *
 * @example
 * createBatches([1,2,3,4,5], 2);
 * // returns [[1,2], [3,4], [5]]
 *
 * @description
 * Useful for rate-limited APIs or controlled parallel requests where
 * sending too many items at once can cause throttling. The function
 * preserves original order and ensures each batch has at most `size` items.
 */
export function createBatches<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
}
